import React from 'react'
import {
    Box,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    Chip,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import DeleteIcon from '@material-ui/icons/Delete'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ShareIcon from '@material-ui/icons/Share'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'
import GavelIcon from '@material-ui/icons/Gavel'
import { CompanyDocument } from '../../models/Company/types'

function isPdfDocument(doc: CompanyDocument): boolean {
    const name = (doc.name || '').toLowerCase()
    const path = (doc.document || '').toLowerCase()
    return name.endsWith('.pdf') || path.includes('.pdf')
}

const MAX_PROPERTY_NAMES_VISIBLE = 2

function PropertyNamesCell({ names }: { names: string[] }) {
    if (!names.length) return <>—</>
    if (names.length <= MAX_PROPERTY_NAMES_VISIBLE) {
        return <>{names.join(', ')}</>
    }
    const visible = names.slice(0, MAX_PROPERTY_NAMES_VISIBLE).join(', ')
    const rest = names.length - MAX_PROPERTY_NAMES_VISIBLE
    return (
        <Tooltip title={names.join(', ')} placement="top" enterDelay={200}>
            <span style={{ cursor: 'default' }}>
                {visible}
                <Typography
                    component="span"
                    color="primary"
                    style={{
                        fontWeight: 600,
                        marginLeft: 4,
                        cursor: 'default',
                    }}
                >
                    +{rest} more
                </Typography>
            </span>
        </Tooltip>
    )
}

interface DocumentsTableProps {
    documents: CompanyDocument[]
    onDelete: (id: number) => void
    onView: (document: CompanyDocument) => void
    onEdit: (document: CompanyDocument) => void
    onShare: (document: CompanyDocument) => void
    onReanalyze: (id: number) => void
    onMakeSignable: (document: CompanyDocument) => void
    promotingIds?: Set<number>
    processingIds?: Set<number>
    // Pagination props
    totalCount: number
    page: number
    rowsPerPage: number
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => void
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        flex: 1,
        maxHeight: 'calc(100vh - 550px)',
        overflow: 'auto',
    },
    table: {
        minWidth: 780,
        height: '100px',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    iconCell: {
        width: 50,
    },
    documentIcon: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.light + '20',
        color: theme.palette.primary.main,
    },
    nameCell: {
        fontWeight: 500,
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '14px',
        maxWidth: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontSize: '14px',
        fontStyle: 'italic',
    },
    dateCell: {
        color: theme.palette.text.secondary,
        fontSize: '14px',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    actionButton: {
        padding: theme.spacing(1),
    },
    shareButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    editButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    viewButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    reanalyzeButton: {
        color: theme.palette.secondary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.light + '20',
        },
    },
    signableButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    deleteButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error.light + '20',
        },
    },
    signableChip: {
        height: 22,
        fontSize: 11,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
    },
    emptyText: {
        color: theme.palette.text.secondary,
    },
    pagination: {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

export const DocumentsTable: React.FC<DocumentsTableProps> = ({
    documents,
    onDelete,
    onView,
    onEdit,
    onShare,
    onReanalyze,
    onMakeSignable,
    promotingIds = new Set(),
    processingIds = new Set(),
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const classes = useStyles()

    const formatDate = (dateString: string) => {
        // Parse date parts directly to avoid timezone conversion issues
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }

    if (totalCount === 0) {
        return (
            <Box className={classes.emptyState}>
                <Typography className={classes.emptyText}>
                    No documents found.
                </Typography>
            </Box>
        )
    }

    return (
        <Paper className={classes.wrapper}>
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 50 }}
                            />
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 300 }}
                            >
                                Document Name
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 120 }}
                            >
                                Type
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 180 }}
                            >
                                Properties
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Summary
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Created
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 220 }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((doc) => (
                            <TableRow
                                key={doc.id}
                                className={classes.row}
                                onClick={() => onView(doc)}
                            >
                                <TableCell className={classes.iconCell}>
                                    <Box className={classes.documentIcon}>
                                        <DescriptionIcon
                                            style={{ fontSize: 18 }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.nameCell}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 8 }}
                                    >
                                        <span>{doc.name}</span>
                                        {doc.share_summary?.label && (
                                            <Chip
                                                size="small"
                                                label={doc.share_summary.label}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {doc.signable_doc?.status ===
                                            'READY' && (
                                            <Chip
                                                size="small"
                                                label="Signable"
                                                color="primary"
                                                className={classes.signableChip}
                                            />
                                        )}
                                        {doc.signable_doc?.status ===
                                            'DRAFT' && (
                                            <Chip
                                                size="small"
                                                label="Signing setup"
                                                variant="outlined"
                                                className={classes.signableChip}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.summaryCell}>
                                    {doc.document_type_detail?.name ??
                                        'General'}
                                </TableCell>
                                <TableCell className={classes.summaryCell}>
                                    <PropertyNamesCell
                                        names={doc.company_names ?? []}
                                    />
                                </TableCell>
                                <TableCell className={classes.summaryCell}>
                                    {processingIds.has(doc.id) ? (
                                        <Box
                                            className={
                                                classes.analyzingContainer
                                            }
                                        >
                                            <CircularProgress size={16} />
                                            <Typography
                                                className={
                                                    classes.analyzingText
                                                }
                                            >
                                                Analyzing document...
                                            </Typography>
                                        </Box>
                                    ) : (
                                        doc.ai_summary || 'No summary available'
                                    )}
                                </TableCell>
                                <TableCell className={classes.dateCell}>
                                    {formatDate(doc.created_date)}
                                </TableCell>
                                <TableCell
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Box className={classes.actions}>
                                        <Tooltip title="Share with tenants">
                                            <IconButton
                                                size="small"
                                                onClick={() => onShare(doc)}
                                                className={`${classes.actionButton} ${classes.shareButton}`}
                                            >
                                                <ShareIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {isPdfDocument(doc) && (
                                            <Tooltip
                                                title={
                                                    doc.signable_doc
                                                        ? doc.signable_doc
                                                              .status ===
                                                          'READY'
                                                            ? 'Open signing template'
                                                            : 'Continue signing setup'
                                                        : 'Make signable'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onMakeSignable(doc)
                                                        }
                                                        className={`${classes.actionButton} ${classes.signableButton}`}
                                                        disabled={promotingIds.has(
                                                            doc.id,
                                                        )}
                                                    >
                                                        {promotingIds.has(
                                                            doc.id,
                                                        ) ? (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        ) : (
                                                            <GavelIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => onEdit(doc)}
                                                className={`${classes.actionButton} ${classes.editButton}`}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => onView(doc)}
                                                className={`${classes.actionButton} ${classes.viewButton}`}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reanalyze">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onReanalyze(doc.id)
                                                }
                                                className={`${classes.actionButton} ${classes.reanalyzeButton}`}
                                                disabled={processingIds.has(
                                                    doc.id,
                                                )}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => onDelete(doc.id)}
                                                className={`${classes.actionButton} ${classes.deleteButton}`}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.pagination}
                component="div"
                count={totalCount}
                page={page}
                onChangePage={onPageChange}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={onRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
            />
        </Paper>
    )
}
